import { mdiDotsVertical, mdiShieldCheck } from '@mdi/js';
import MenuItem from '@mui/material/MenuItem';
import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { Chip } from 'common/components/Chip';
import { Icon } from 'common/components/Icon';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { Switch } from 'common/components/FormField';
import { IconButton } from 'common/components/IconButton';
import {
    ListItem,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { getExpiryDisplay } from 'common/utils/expiryScale';
import { ICertificateTypeListItemFragment } from 'graphql/types';

interface IProps extends React.ComponentProps<typeof ListItem> {
    certificate: ICertificateTypeListItemFragment;
    isMandatoryFieldName: string;
    onDelete?(certificate: ICertificateTypeListItemFragment): void;
}

export const SkillCertificateFieldListItem = ({
    certificate,
    isMandatoryFieldName,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { getFieldProps } = useFormikContext();
    const { id, name, thumbnail, isExternalCertificate, expiry, expiryScale } =
        certificate || {};
    const isMandatory = !!getFieldProps(isMandatoryFieldName).value;

    const chipLabel = isExternalCertificate
        ? translate('externalChip')
        : expiry && expiryScale
          ? getExpiryDisplay(expiry, expiryScale)
          : undefined;

    return (
        <ListItem button disableHover key={id} px={0} {...other}>
            <ListItemMedia
                color="primary"
                image={thumbnail?.url || ''}
                size="small"
            >
                <Icon path={mdiShieldCheck} size="3rem" />
            </ListItemMedia>

            <ListItemText
                primary={name}
                sx={{ overflow: 'visible', mb: '-9px' }}
            >
                <Field
                    checked={isMandatory}
                    component={Switch}
                    formControlProps={{
                        margin: 'none',
                        sx: { mt: '-4px', mb: '-9px' },
                    }}
                    label={translate('skillCertificateField.mandatory')}
                    name={isMandatoryFieldName}
                />
            </ListItemText>

            {chipLabel && <Chip label={chipLabel} />}

            <ListItemSecondaryAction>
                <DropdownMenu
                    anchor={
                        <IconButton
                            color="inherit"
                            iconPath={mdiDotsVertical}
                            iconSize="2.4rem"
                            sx={{ ml: 2 }}
                        />
                    }
                >
                    <MenuItem onClick={() => onDelete?.(certificate)}>
                        {translate('delete')}
                    </MenuItem>
                </DropdownMenu>
            </ListItemSecondaryAction>
        </ListItem>
    );
};
